import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import {
  Badge, Button, CardSection, EmptyState, Field, LoadingRows, Notice, Panel, SearchCombobox, SelectField, TextAreaField,
} from "./ui.jsx";

function formatDate(value) {
  if (!value) return "No due date";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function toLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function TaskComposer({ companyId, personId, salesMotionId, members, companies = [], people = [], onCreated, onCancel }) {
  const [form, setForm] = useState({
    title: "", description: "", task_type: "follow_up", priority: "normal", due_at: "", owner_membership_id: "",
    company_id: companyId || "", person_id: personId || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.createTask({
        title: form.title,
        description: form.description || null,
        task_type: form.task_type,
        priority: form.priority,
        due_at: form.due_at || null,
        owner_membership_id: form.owner_membership_id || null,
        company_id: companyId || form.company_id || null,
        person_id: personId || form.person_id || null,
        sales_motion_id: salesMotionId || null,
      });
      onCreated();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
        <div className="col-span-full grid gap-1"><h3 className="text-lg font-bold">Create task</h3><p className="text-xs leading-5 text-muted-foreground">This becomes the canonical next action for the linked record.</p></div>
        {error && <div className="col-span-full"><Notice>{error}</Notice></div>}
        <div className="col-span-full"><Field label="Task" value={form.title} onChange={set("title")} autoFocus required /></div>
        <SelectField label="Type" value={form.task_type} onChange={set("task_type")}><option value="follow_up">Follow-up</option><option value="call">Call</option><option value="email">Email</option><option value="meeting">Meeting</option><option value="research">Research</option><option value="general">General</option></SelectField>
        <SelectField label="Priority" value={form.priority} onChange={set("priority")}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></SelectField>
        {!companyId && <SearchCombobox label="Company" hint="optional if a person is selected" value={form.company_id} onChange={(company_id) => setForm((current) => ({ ...current, company_id }))} options={companies.map((company) => ({ value: company.id, label: company.name, description: company.domain || company.website_url || "Company" }))} placeholder="Search companies…" emptyMessage="No matching companies" />}
        {!personId && <SearchCombobox label="Person" hint="optional if a company is selected" value={form.person_id} onChange={(person_id) => setForm((current) => ({ ...current, person_id }))} options={people.map((person) => ({ value: person.id, label: person.name, description: person.title || person.business_email || "Person" }))} placeholder="Search people…" emptyMessage="No matching people" />}
        <Field label="Due" hint="optional" type="datetime-local" value={form.due_at} onChange={set("due_at")} />
        <SelectField label="Owner" hint="defaults to you" value={form.owner_membership_id} onChange={set("owner_membership_id")}><option value="">Current user</option>{members.map((member) => <option key={member.membership_id} value={member.membership_id}>{member.name}</option>)}</SelectField>
        <div className="col-span-full"><TextAreaField label="Notes" hint="optional" className="min-h-20" value={form.description} onChange={set("description")} /></div>
        <div className="col-span-full flex justify-end gap-2"><Button onClick={onCancel}>Cancel</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "Creating…" : "Create task"}</Button></div>
      </form>
    </Panel>
  );
}

export function TaskRows({ tasks, onChanged, onOpenCompany, onOpenPerson, onOpenLead }) {
  const [acting, setActing] = useState(null);
  async function setStatus(task, status) {
    setActing(`${task.id}:${status}`);
    try { await api.updateTask(task.id, { status }); await onChanged(); } finally { setActing(null); }
  }
  const headerCell = "border-b border-border border-r border-r-neutral-100 px-3 font-semibold";
  const cell = "border-r border-neutral-100 px-3 py-2.5 align-top";
  return (
    <div className="overflow-x-auto rounded-lg border border-neutral-100">
      <table className="w-full min-w-[760px] border-collapse text-[13px]">
        <thead>
          <tr className="h-10 bg-muted/50 text-left text-[11px] uppercase tracking-[.06em] text-muted-foreground">
            <th className={`${headerCell} w-10 px-2.5`}><span className="sr-only">Status</span></th>
            <th className={`${headerCell} min-w-[240px]`}>Task</th>
            <th className={`${headerCell} min-w-[150px]`}>Linked to</th>
            <th className={`${headerCell} min-w-[130px]`}>Due</th>
            <th className={`${headerCell} min-w-[110px]`}>Owner</th>
            <th className={`${headerCell} min-w-[90px]`}>Priority</th>
            <th className="w-[170px] border-b border-border px-3"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>{tasks.map((task) => (
          <tr className="border-b border-neutral-100 transition-colors last:border-b-0 hover:bg-muted/50" key={task.id}>
            <td className={`${cell} px-2.5`}>
              <button
                type="button"
                aria-label={task.status === "completed" ? `Reopen ${task.title}` : `Complete ${task.title}`}
                className={task.status === "completed" ? "mt-0.5 grid size-[17px] shrink-0 cursor-pointer place-items-center rounded-full bg-green-500 text-[10px] leading-none text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" : "mt-0.5 block size-[17px] shrink-0 cursor-pointer rounded-full border-[1.5px] border-neutral-400 transition-colors hover:border-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"}
                disabled={Boolean(acting)}
                onClick={() => setStatus(task, task.status === "open" ? "completed" : "open")}
              >{task.status === "completed" ? "✓" : ""}</button>
            </td>
            <td className={cell}>
              <div className="grid min-w-0 gap-0.5">
                <span className={task.status === "completed" ? "text-[13px] font-medium text-muted-foreground line-through" : "text-[13px] font-semibold text-foreground"}>{task.title}</span>
                <small className="text-[11px] text-muted-foreground">{task.task_type.replaceAll("_", " ")}{task.status !== "open" ? ` · ${task.status}` : ""}</small>
                {task.description && <p className="line-clamp-1 text-xs leading-5 text-muted-foreground">{task.description}</p>}
              </div>
            </td>
            <td className={cell}>
              <div className="grid justify-items-start gap-0.5 text-xs">
                {task.sales_motion && <button className="max-w-full truncate font-medium text-primary hover:text-primary-600" type="button" onClick={() => onOpenLead?.(task.sales_motion.id)}>{task.sales_motion.name} →</button>}
                {task.company && <button className="max-w-full truncate font-medium text-primary hover:text-primary-600" type="button" onClick={() => onOpenCompany?.(task.company.id)}>{task.company.name} →</button>}
                {task.person && <button className="max-w-full truncate font-medium text-primary hover:text-primary-600" type="button" onClick={() => onOpenPerson?.(task.person.id)}>{task.person.name} →</button>}
                {!task.sales_motion && !task.company && !task.person && <span className="text-muted-foreground">—</span>}
              </div>
            </td>
            <td className={cell}>
              <div className="grid gap-0.5 text-xs">
                <span className={task.overdue ? "font-semibold text-destructive" : "text-neutral-600"}>{formatDate(task.due_at)}</span>
                {task.overdue && <small className="text-[11px] text-destructive">Past due</small>}
              </div>
            </td>
            <td className={`${cell} text-xs text-neutral-600`}>{task.owner?.name || "Unassigned"}</td>
            <td className={cell}>
              <div className="flex flex-wrap gap-1">
                <Badge status={task.priority === "high" ? "running" : undefined}>{task.priority}</Badge>
                {task.overdue && <Badge status="failed">overdue</Badge>}
              </div>
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right align-top">
              {task.status === "open" ? <><Button variant="ghost" className="min-h-7 px-2 text-[11px]" disabled={Boolean(acting)} onClick={() => setStatus(task, "cancelled")}>Cancel</Button><Button variant="primary" className="min-h-7 px-2.5 text-[11px]" disabled={Boolean(acting)} onClick={() => setStatus(task, "completed")}>{acting === `${task.id}:completed` ? "Completing…" : "Complete"}</Button></> : <Button className="min-h-7 px-2.5 text-[11px]" disabled={Boolean(acting)} onClick={() => setStatus(task, "open")}>Reopen</Button>}
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function TasksPanel({ companyId, personId, salesMotionId, title = "Tasks", onOpenCompany, onOpenPerson, onOpenLead, onTimelineChanged }) {
  const [state, setState] = useState({ loading: true, tasks: [], members: [], error: "" });
  const [creating, setCreating] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const [taskData, memberData] = await Promise.all([
        api.tasks({ status: "all", ...(companyId ? { company_id: companyId } : {}), ...(personId ? { person_id: personId } : {}), ...(salesMotionId ? { sales_motion_id: salesMotionId } : {}), limit: 100 }),
        api.members(),
      ]);
      setState({ loading: false, tasks: taskData.tasks, members: memberData.users, error: "" });
    } catch (error) { setState((current) => ({ ...current, loading: false, error: error.message })); }
  }, [companyId, personId, salesMotionId]);
  useEffect(() => { load(); }, [load]);
  const visible = useMemo(() => showClosed ? state.tasks : state.tasks.filter((task) => task.status === "open"), [showClosed, state.tasks]);
  const openCount = state.tasks.filter((task) => task.status === "open").length;
  async function changed() { await load(); onTimelineChanged?.(); }
  return (
    <div className="grid gap-5">
      {creating && <TaskComposer companyId={companyId} personId={personId} salesMotionId={salesMotionId} members={state.members} onCancel={() => setCreating(false)} onCreated={async () => { setCreating(false); await changed(); }} />}
      <CardSection title={`${title} (${openCount} open)`} meta="Due work is the canonical next action" action={<div className="flex flex-wrap gap-2"><Button variant="ghost" onClick={() => setShowClosed((value) => !value)}>{showClosed ? "Hide closed" : "Show history"}</Button><Button variant="primary" onClick={() => setCreating((value) => !value)}>{creating ? "Close" : "Add task"}</Button></div>}>
        {state.error && <Notice>{state.error}</Notice>}
        {state.loading && !state.tasks.length ? <LoadingRows count={3} /> : !visible.length ? <EmptyState title={showClosed ? "No tasks yet" : "No open tasks"} description="Create a follow-up, call, email, meeting, or research task. Completed work remains in history and appears in unified activity." /> : <TaskRows tasks={visible} onChanged={changed} onOpenCompany={onOpenCompany} onOpenPerson={onOpenPerson} onOpenLead={onOpenLead} />}
      </CardSection>
    </div>
  );
}

export { TaskComposer, formatDate, toLocalInput };
